<template>
  <div class="pb-page narrow bsv">
    <router-link :to="multiBrand || limits.brands > 1 ? '/brands' : '/settings'" class="pb-button link pb-back bsv-back"><i class="mdi mdi-arrow-left" aria-hidden="true"></i> {{ multiBrand || limits.brands > 1 ? 'Brands' : 'Settings' }}</router-link>
    <div v-if="loadError" class="pb-alert is-error" role="alert"><span>{{ loadError }}</span></div>
    <template v-else-if="brand">
      <PageHeader :title="item ? item.name : 'Brand'" subtitle="How this brand appears on its collection pages, widgets and hosted wall." />
      <div v-if="item && item.admin_paused" class="pb-alert is-error" role="status"><i class="mdi mdi-pause-octagon-outline pb-ico" aria-hidden="true"></i>
        <span>Public pages are paused by a TrustClap administrator<span v-if="item.admin_reason">: {{ item.admin_reason }}</span>. Contact support if you think this is a mistake.</span></div>

      <form v-if="item" class="pb-card bsv-form" @submit.prevent="save">
        <div class="pb-field"><label class="pb-label" for="bsv-name">Name</label><input id="bsv-name" v-model="form.name" class="pb-input" maxlength="120" required :disabled="!canEdit"></div>
        <div class="pb-field"><label class="pb-label" for="bsv-site">Website <span class="optional">(optional)</span></label>
          <input id="bsv-site" v-model="form.website_url" class="pb-input" maxlength="500" placeholder="yourbusiness.com" :disabled="!canEdit"></div>
        <div class="pb-field"><label class="pb-label" for="bsv-desc">Short description <span class="optional">(optional)</span></label>
          <textarea id="bsv-desc" v-model="form.description" class="pb-textarea" rows="2" maxlength="600" :disabled="!canEdit"></textarea></div>
        <div class="pb-field"><span id="bsv-color-l" class="pb-label">Colour</span>
          <div class="pb-row bsv-colors" role="radiogroup" aria-labelledby="bsv-color-l">
            <button v-for="swatch in swatches" :key="swatch.hex" type="button" role="radio" :aria-checked="form.color === swatch.hex ? 'true' : 'false'"
                    :aria-label="swatch.name" :disabled="!canEdit" :class="['bsv-swatch', { on: form.color === swatch.hex }]" :style="{ background: swatch.hex }" @click="form.color = swatch.hex"></button>
            <label class="pb-sr-only" for="bsv-color">Any colour</label>
            <input id="bsv-color" type="color" :value="form.color" class="bsv-picker" :disabled="!canEdit" @input="form.color = $event.target.value.toUpperCase()">
            <span class="pb-mono bsv-hex">{{ form.color }}</span>
          </div>
          <span class="pb-help">Buttons and accents on your public pages use it; text is darkened automatically when needed so it stays readable.</span></div>
        <div class="pb-field"><span class="pb-label">Logo</span>
          <LogoPicker v-model="logo" :current="logoUrl" :status="logoStatus" @input="uploadLogo" />
          <button v-if="item.logo && canEdit" type="button" class="pb-button sm ghost bsv-remove-logo" @click="removeLogo">Remove logo</button></div>
        <div class="pb-field"><label class="pb-label" for="bsv-privacy">Your privacy policy link <span class="optional">(recommended)</span></label>
          <input id="bsv-privacy" v-model="form.privacy_url" class="pb-input" maxlength="500" placeholder="yourbusiness.com/privacy" :disabled="!canEdit">
          <span class="pb-help">Collection pages link to it next to TrustClap's own privacy notice.</span></div>
        <div class="pb-field"><label class="pb-label" for="bsv-slug">Hosted wall address</label>
          <div class="bsv-slug"><span class="pb-mono">{{ origin }}/w/</span><input id="bsv-slug" v-model="form.slug" class="pb-input" maxlength="40" :disabled="!canEdit"></div>
          <span class="pb-help">3 to 40 lowercase letters, numbers and hyphens. Changing it breaks links you already shared.</span></div>
        <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
        <div v-if="canEdit" class="pb-actions"><button type="submit" class="pb-button" :disabled="saving || !dirty">{{ saving ? 'Saving…' : 'Save changes' }}</button></div>
      </form>

      <section v-if="item && can('reviewers')" class="pb-card bsv-approval" aria-labelledby="bsv-appr">
        <h2 id="bsv-appr" class="pb-section-title display-font">Client approval</h2>
        <label class="pb-check bsv-switch"><input type="checkbox" :checked="item.requires_client_approval" @change="toggleApproval($event)">Require client approval before publishing
          <PlanChip v-if="!limits.client_approvals" :plan="availableOn('client_approvals')" /></label>
        <p class="pb-help">Approved feedback goes to your client's private review link first. <router-link to="/team">Manage client reviewers</router-link></p>
      </section>

      <section v-if="item && can('brands')" class="pb-card bsv-archive" aria-labelledby="bsv-arch">
        <h2 id="bsv-arch" class="pb-section-title display-font">{{ item.status === 'active' ? 'Archive this brand' : 'Archived' }}</h2>
        <p class="pb-body">An archived brand serves nothing publicly and doesn't count toward your plan. Its feedback stays here and in exports.</p>
        <button v-if="item.status === 'active'" type="button" class="pb-button danger" @click="archive">Archive brand</button>
        <button v-else type="button" class="pb-button outline" @click="restore">Restore brand</button>
      </section>
    </template>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import LogoPicker from '../components/LogoPicker.vue';
import PageHeader from '../components/PageHeader.vue';
import PlanChip from '../components/PlanChip.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import workspace from '../mixins/workspace';
import { get, patch, post, remove, message, planLimit } from '../api';
import { SWATCHES } from '../lib/color';
import { uploadImage, waitForImage } from '../lib/images';
import { ask } from '../lib/confirm';
import { toast } from '../lib/toast';
import { imageUrl } from '../publicUrls';

export default {
  name: 'BrandSettingsView',
  components: { LogoPicker, PageHeader, PlanChip, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ item: null, form: {}, saved: '', loadError: '', error: '', saving: false, logo: null, logoStatus: '', swatches: SWATCHES,
    upgrade: null, upgradeOpen: false }),
  computed: {
    canEdit() { return this.can('brands'); },
    origin() { return window.location.origin.replace(/^https?:\/\//, ''); },
    logoUrl() { return this.item && this.item.logo && this.item.logo.status === 'complete' ? imageUrl(this.item.logo.key) : ''; },
    dirty() { return JSON.stringify(this.form) !== this.saved; }
  },
  watch: { '$route.params.id'() { this.load(); } },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.load();
  },
  methods: {
    take(item) {
      this.item = item;
      this.form = { name: item.name, website_url: item.website_url || '', description: item.description || '', color: item.color,
        privacy_url: item.privacy_url || '', slug: item.slug };
      this.saved = JSON.stringify(this.form);
    },
    async load() {
      this.loadError = '';
      try { this.take(await get('/brands/' + this.$route.params.id)); } catch (error) { this.loadError = message(error, 'Brand not found.'); }
    },
    async save() {
      this.saving = true;
      this.error = '';
      try {
        const body = Object.assign({}, this.form);
        ['website_url', 'description', 'privacy_url'].forEach(key => { body[key] = (body[key] || '').trim() || null; });
        this.take(await patch('/brands/' + this.item.id, body));
        await this.refreshWorkspace();
        toast('Saved.');
      } catch (error) { this.error = message(error); } finally { this.saving = false; }
    },
    async uploadLogo(picked) {
      if (!picked || !picked.file) return;
      this.logoStatus = 'Uploading…';
      try {
        const image = await uploadImage('/brands/' + this.item.id + '/logo', picked.file);
        this.logoStatus = 'Processing…';
        const done = await waitForImage(image, current => { this.logoStatus = current.status === 'complete' ? '' : 'Processing…'; });
        if (done && done.status === 'failed') this.logoStatus = done.error || 'That image couldn\'t be read. Try a JPG or PNG.';
        else { this.logoStatus = ''; this.logo = null; }
        await this.load();
        await this.refreshWorkspace();
      } catch (error) { this.logoStatus = message(error); }
    },
    async removeLogo() {
      try { this.take(await remove('/brands/' + this.item.id + '/logo')); await this.refreshWorkspace(); toast('Logo removed.'); } catch (error) { toast(message(error), 'error'); }
    },
    async toggleApproval(event) {
      const wanted = event.target.checked;
      if (wanted && !this.limits.client_approvals) {
        event.target.checked = false;
        this.upgrade = { feature: 'client_approvals' };
        this.upgradeOpen = true;
        return;
      }
      try {
        this.take(await patch('/brands/' + this.item.id + '/approval', { requires_client_approval: wanted }));
        await this.refreshWorkspace();
        toast(wanted ? 'Client approval is on for this brand.' : 'Client approval is off.');
      } catch (error) {
        event.target.checked = !wanted;
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else toast(message(error), 'error');
      }
    },
    async archive() {
      if (!(await ask('Archive ' + this.item.name + '? Its widgets, wall and collection pages stop serving.', { action: 'Archive', tone: 'danger' }))) return;
      try { this.take(await post('/brands/' + this.item.id + '/archive')); await this.refreshWorkspace(); toast('Archived.'); } catch (error) { toast(message(error), 'error'); }
    },
    async restore() {
      try { this.take(await post('/brands/' + this.item.id + '/restore')); await this.refreshWorkspace(); toast('Restored.'); } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else toast(message(error), 'error');
      }
    }
  }
};
</script>

<style scoped>
.bsv-back{margin-bottom:16px}
.bsv-form{margin-bottom:18px}
.bsv-colors{flex-wrap:wrap;gap:8px}
.bsv-swatch{width:40px;height:40px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 1px #D4C9B8;cursor:pointer}
.bsv-swatch.on{box-shadow:0 0 0 2px #1F2328}
.bsv-picker{width:44px;height:44px;padding:2px;border:1px solid #8C8378;border-radius:10px;background:#fff;cursor:pointer}
.bsv-hex{font-size:13px;color:#4F4A45}
.bsv-remove-logo{margin-top:8px}
.bsv-slug{display:flex;align-items:center;gap:6px}
.bsv-slug .pb-mono{font-size:13px;color:#6B635B;white-space:nowrap}
.bsv-approval,.bsv-archive{margin-bottom:18px}
.bsv-approval .pb-section-title,.bsv-archive .pb-section-title{margin-bottom:12px}
.bsv-archive .pb-body{margin-bottom:14px}
@media(max-width:600px){.bsv-slug{flex-direction:column;align-items:stretch}}
</style>
